import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BellFilled,
  CloseOutlined,
  FullscreenOutlined,
} from '@ant-design/icons';
import { Button, Col, Dropdown, Flex, message, Row, Spin, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateToExternalMapView } from '../utils/navigateToExternalMapView';
import {
  DownloadCOC,
  GetCOCExportCount,
  GetShipmentsViewers,
  legTypes,
  ViewerData,
} from '../../../apis/shipment';
import type { GeoFenceStop, ShipmentLocationData } from '@/apis/types';
import { GetWsTicket } from '../../../apis/auth';
import { getSessionSignal } from '@/common/interceptor/sessionAbortController';
import {
  ArchiveIcon,
  CalendarIcon,
  CheckmarkIcon,
  ChevronRightIcon,
  CopyIcon,
  DotsIcon,
  EditLocationAltIcon,
  LinkIcon,
  RouteIcon,
  MuteBellIcon,
  OneTimeArrowIcon,
  LeftTailArrowIcon,
  UploadSmallIcon,
  HomeTemperatureIcon,
} from '../../../assets/icons/icons';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';
import { truncateByLength, useToken } from '../../../common/helper';
import {
  CustomShipmentText,
  EllipsisTooltip,
  TruncateWithTooltip,
} from '../../../components';
import shipmentWebSocketService from '../../../context/shipmentWebSocketService';
import CircleGroup from '../partials/CircleGroup';
import PerShipmentNotificationModal from '../PerShipmentNotification/PerShipmentNotificationModal';
import type { NotificationData } from '../PerShipmentNotification/OneTimeNotificationModal';
import { SingleShipmentDataExtended, TeamData } from '@/apis/types';
import ExportCOCDropdown from './ExportCOCDropdown';
import { saveBlobAsFile } from './fileDownload';
import TeamListWithDots from './TeamListWithDots';
import { useShipmentPermissions } from '../ControlTower/hooks/useShipmentPermissions';
import { getShipmentLegModeIcon, PencilIcon } from '@/assets/icons/icons';
import { getLegModeIconType } from '../ControlTower/components/shipment/legModeUtils';
import { isFedExShipment, isUPSShipment } from '../shellShipmentHelpers';
import {
  getPrimarySensorPoints,
  type SensorTrackPoint,
  type SensorTrackShipment,
} from '../mapSensorUtils';
import FedExPickupControls, {
  canOfferFedExPickup,
} from './FedExPickupControls';
import { canSchedulePickupFromPrefill } from './fedexPickupUtils';
import { usePickupPrefill } from '@/hooks/useFedExPickup';
import { useFedExPickupGate } from '@/hooks/useFedExPickupGate';
import { useRouteDeviationGate } from '@/hooks/useRouteDeviationGate';
import GeoFenceLocationsModal, {
  GeoFenceDraft,
  GeoFenceLocationOption,
} from './GeoFenceLocationsModal';
import RouteDeviationSetupModal from './RouteDeviation/RouteDeviationSetupModal';
import ArrowBack from '@/assets/images/ArrowBack';
import useFetchGeoFenceStops from '../hooks/useFetchGeoFenceStops';
import { FeatureLabels, MenuLabels, SubMenuLabels } from '@/common/enum';
import { useFeaturePermissions, useMenuPermissions } from '@/hooks';

const DEFAULT_GEO_FENCE_CENTER = { lat: 37.7749, lng: -122.4194 };

const mapGeoFenceStopToLocation = (
  stop: GeoFenceStop,
  index: number
): GeoFenceLocationOption => {
  const name = stop.name?.trim() || `Location ${index + 1}`;
  const lat = stop.latlng?.lat;
  const lng = stop.latlng?.lng;

  return {
    id: stop.stop_key,
    stopKey: stop.stop_key,
    menuLabel: stop.label,
    name,
    address: stop.address ?? '',
    coordinates:
      lat !== undefined &&
      lng !== undefined &&
      Number.isFinite(lat) &&
      Number.isFinite(lng)
        ? { lat, lng }
        : DEFAULT_GEO_FENCE_CENTER,
    locationId: stop.location_id ?? null,
    executionLegId: stop.execution_leg_id ?? null,
    hasDefaultFence: stop.has_default_geofence,
    hasPerShipmentFence: stop.has_per_shipment_geofence,
  };
};

export interface ExportItemsTypes {
  key?: string;
  label: string;
  disable?: boolean;
}

export interface COCExportCounts {
  selected_shipments?: number;
  shipments_with_pod?: number;
  status_pdf_count?: number;
  pod_pdf_count?: number;
  total_pdf_count?: number;
  missing_pods_count?: number;
  requires_email?: boolean;
  status_updates_exist?: boolean;
  comments_exist?: boolean;
  alerts_exist?: boolean;
}

interface ShipmentHeaderProps {
  userId?: string | number;
  checkTokenExpiry: () => string;
  alertsArray?: unknown[];
  referenceNumber?: string;
  legContextLabel?: string;
  shipmentId: string;
  hasNotification?: boolean;
  handleMenuClick: (menuInfo: { key: string }) => void;
  supportsSchedulePickup?: boolean;
  toggleDrawer: (open: boolean | string, type?: string, id?: string) => void;
  handleShareShipmentModal: () => void;
  unauthorize: (error: unknown) => void;
  isFromHome?: boolean;
  teams: TeamData[];
  initialAction?: string;
  /** When true, the title bar shows Quote and Book action buttons under the
   *  reference number (pre-pickup shells only; parent gates with
   *  canQuoteOrBookShell). */
  isShell?: boolean;
  /** Invoked when the user clicks Quote on a shell shipment. */
  onQuoteShell?: () => void;
  /** Invoked when the user clicks Book on a shell shipment. */
  onBookShell?: () => void;
  shipmentData?: SingleShipmentDataExtended;
  legType?: legTypes;
  /** Execution Leg's mode (Air/Cartage/Linehaul/Clearance), used to render a
   *  mode icon next to the MBL reference at the top of the EL panel. */
  legMode?: {
    value?: string;
    name?: string | null;
    icon_token?: string;
  } | null;
  /** When set (this panel was opened from another CL/EL panel, in either
   *  direction), shows a back-navigation row at the top of the header
   *  displaying the originating panel's reference ID. */
  backReferenceLabel?: string;
  /** Callback invoked when the user clicks the back-nav row to return to the
   *  panel that opened this one. */
  onBackToParent?: () => void;
  isFromActivityFeed?: boolean;
}

/**
 * ShipmentHeader component that displays header information and actions for a shipment
 *
 * @param userId - Current user ID
 * @param checkTokenExpiry - Function to get valid token
 * @param alertsArray - Array of alerts for the shipment
 * @param referenceNumber - Shipment reference number
 * @param shipmentId - Unique shipment identifier
 * @param hasNotification - Whether shipment has notifications
 * @param handleMenuClick - Handler for dropdown menu clicks
 * @param toggleDrawer - Handler for opening/closing drawers
 * @param handleShareShipmentModal - Handler for share modal
 * @param unauthorize - Handler for authorization errors
 * @param isFromHome - Whether component is rendered from home page
 */
const ShipmentHeader: React.FC<ShipmentHeaderProps> = ({
  userId,
  checkTokenExpiry,
  alertsArray,
  referenceNumber,
  legContextLabel,
  shipmentId,
  hasNotification,
  handleMenuClick,
  supportsSchedulePickup,
  toggleDrawer,
  handleShareShipmentModal,
  unauthorize,
  isFromHome,
  teams,
  initialAction,
  isShell = false,
  onQuoteShell,
  onBookShell,
  shipmentData,
  legType = 'CL',
  legMode,
  backReferenceLabel,
  onBackToParent,
  isFromActivityFeed, //Users Activity, separate "User activity" back-arrow
}) => {
  const isNotificationMuted = Boolean(shipmentData?.mute_notification);
  const { token, decodedUserToken } = useToken();
  const { hasOperationalLegView } = useShipmentPermissions();
  const decodedToken = decodedUserToken();
  const enabledGxpFeature = Boolean(decodedToken?.temperature_gxp_enabled);
  const [viewers, setViewers] = useState<ViewerData[]>([]);
  const [openNotificationModal, setOpenNotificationModal] = useState(false);
  const [geoFenceModalOpen, setGeoFenceModalOpen] = useState(false);
  const [routeDeviationModalOpen, setRouteDeviationModalOpen] = useState(false);
  const [selectedGeoFenceLocation, setSelectedGeoFenceLocation] =
    useState<GeoFenceLocationOption | null>(null);
  const [geoFenceDrafts, setGeoFenceDrafts] = useState<
    Record<string, GeoFenceDraft>
  >({});
  const [headerMenuView, setHeaderMenuView] = useState<
    'main' | 'geoFence' | 'exportCoc'
  >('main');
  /** Controlled so menu items (e.g. Generate return label) close before a
   *  sibling drawer opens — Ant Dropdown with dropdownRender stays open otherwise. */
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const allPermissions = useMenuPermissions(MenuLabels.Shipment);
  const referenceLabel =
    legType === 'EL' ? 'MBL' : themeT('referenceId', theme.themeType);
  const GxP_permissions = useMenuPermissions(
    MenuLabels.Settings,
    SubMenuLabels.Gxp_reports
  );

  const goToExternalView = () => {
    navigateToExternalMapView(navigate, shipmentId, location.search, legType);
  };

  useEffect(() => {
    const token = checkTokenExpiry();
    if (!token || !shipmentId || decodedToken?.is_preview) {
      shipmentWebSocketService.disconnect();
      return;
    }

    const baseDomain = process.env.REACT_APP_WEB_SOCKETS;
    const signal = getSessionSignal();
    let cancelled = false;

    const onAbort = () => {
      cancelled = true;
      shipmentWebSocketService.disconnect();
    };

    signal.addEventListener('abort', onAbort, { once: true });

    const connect = async () => {
      let url: string;
      try {
        const res = await GetWsTicket();
        const ticket = res?.data?.ticket;
        if (cancelled || signal.aborted) return;
        url = `${baseDomain}/shipment/123/?shipmentId=${shipmentId}&ticket=${ticket}`;
      } catch (error: unknown) {
        const err = error as { code?: string; name?: string; message?: string };
        if (
          cancelled ||
          signal.aborted ||
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          err?.message?.toLowerCase().includes('canceled') ||
          err?.message?.toLowerCase().includes('cancelled')
        ) {
          return;
        }
        url = `${baseDomain}/shipment/123/?shipmentId=${shipmentId}&token=${token}`;
      }
      if (!cancelled && !signal.aborted) {
        shipmentWebSocketService.connect(url);
      }
    };

    connect().catch((error) => console.error(error));

    return () => {
      cancelled = true;
      signal.removeEventListener('abort', onAbort);
      shipmentWebSocketService.disconnect();
    };
  }, [checkTokenExpiry, shipmentId, token, decodedToken?.is_preview]);

  useEffect(() => {
    if (legType === 'EL' || shipmentData?.leg_type === 'EL') {
      return;
    }
    const fetchViewers = () => {
      const token = checkTokenExpiry();
      if (!shipmentId) return;
      GetShipmentsViewers(token, shipmentId)
        .then((res) => {
          const data = res?.data ? res.data : null;
          if (data) {
            if (data?.data?.is_success) {
              const currentUserId = String(userId ?? userId);
              const list = Array.isArray(data?.data?.data)
                ? data.data.data
                : [];

              const filtered = list.filter(
                (item: ViewerData) => String(item?.created_by) !== currentUserId
              );
              setViewers(filtered);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {});
    };

    fetchViewers();
    const interval = setInterval(() => {
      fetchViewers();
    }, 30000);

    return () => clearInterval(interval);
  }, [
    checkTokenExpiry,
    shipmentId,
    unauthorize,
    userId,
    legType,
    shipmentData?.leg_type,
  ]);

  const featuresPermissions = useFeaturePermissions(
    FeatureLabels.RecordManagement
  );

  // FedEx + UPS. Addresses need not be fully parsed — the return drawer can be
  // filled in manually when prefill is incomplete.
  const canGenerateReturnLabel =
    isFedExShipment(shipmentData) || isUPSShipment(shipmentData);
  const pickupGateOn = useFedExPickupGate();
  const { prefill: pickupPrefill } = usePickupPrefill(
    shipmentData?.id,
    !!supportsSchedulePickup &&
      pickupGateOn &&
      canOfferFedExPickup(shipmentData)
  );
  const canSchedulePickup =
    !!supportsSchedulePickup &&
    pickupGateOn &&
    canOfferFedExPickup(shipmentData) &&
    canSchedulePickupFromPrefill(pickupPrefill);
  const hasShipmentSensor = Boolean(
    shipmentData?.sensor_summary?.[0]?.sensor_id ||
    shipmentData?.sensor?.sensor_id ||
    shipmentData?.sensor?.id
  );
  const canShowGeoFenceFeature =
    decodedToken?.per_shipment_geo_fence_enabled === true &&
    legType === 'CL' &&
    hasShipmentSensor;
  const routeDeviationGateOpen = useRouteDeviationGate();
  // The server refuses a change on a finished shipment, so the entry point is
  // not offered there.
  const isFinishedShipment =
    Boolean(shipmentData?.is_archived) ||
    ['DELIVERED', 'CANCELLED', 'ARCHIVED'].includes(shipmentData?.status ?? '');
  // The server checks update permission on this shipment through its teams, as
  // Edit does, not the global "Record management" feature.
  const canShowRouteDeviation =
    routeDeviationGateOpen &&
    Boolean(shipmentData?.permissions?.is_update) &&
    legType === 'CL' &&
    hasShipmentSensor &&
    !isFinishedShipment;
  const {
    stops: geoFenceStops,
    isLoading: isGeoFenceStopsLoading,
    hasMore: hasMoreGeoFenceStops,
    loadMore: loadMoreGeoFenceStops,
    refetchGeoFenceStops,
  } = useFetchGeoFenceStops(shipmentId, canShowGeoFenceFeature);
  // The lane comes from the shipment already loaded here, so the modal works
  // whether or not the company uses per-shipment geo-fences.
  const routeDeviationLane = useMemo(() => {
    const stopOf = (location?: ShipmentLocationData | null) =>
      location?.name
        ? {
            name: location.name,
            address: location.address,
            latlng: location.latlng,
          }
        : undefined;
    return {
      pickup: stopOf(shipmentData?.from_location),
      delivery: stopOf(shipmentData?.to_location),
    };
  }, [shipmentData?.from_location, shipmentData?.to_location]);
  const routeDeviationPosition = useMemo(() => {
    if (!routeDeviationModalOpen || !shipmentData) return undefined;
    const latest = getPrimarySensorPoints(
      shipmentData as SensorTrackShipment<SensorTrackPoint>
    ).at(-1);
    return latest?.latitude != null && latest.longitude != null
      ? { lat: latest.latitude, lng: latest.longitude }
      : undefined;
  }, [routeDeviationModalOpen, shipmentData]);
  const geoFenceLocations = useMemo(
    () => geoFenceStops.map(mapGeoFenceStopToLocation),
    [geoFenceStops]
  );
  const handleGeoFenceLocationsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const isNearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 12;

      if (isNearBottom && hasMoreGeoFenceStops && !isGeoFenceStopsLoading) {
        loadMoreGeoFenceStops();
      }
    },
    [hasMoreGeoFenceStops, isGeoFenceStopsLoading, loadMoreGeoFenceStops]
  );

  const items = [
    {
      key: 'edit_shipment',
      label: (
        <span className='header-menu-item'>
          <span className='header-menu-icon-slot'>
            <PencilIcon width={12} height={12} color='var(--text-primary)' />
          </span>
          <span className='header-menu-item-content'>Edit shipment</span>
        </span>
      ),
    },
    {
      key: 'archive_shipment',
      label: (
        <span className='header-menu-item'>
          <span className='header-menu-icon-slot'>
            <ArchiveIcon />
          </span>
          <span className='header-menu-item-content'>Archive shipment</span>
        </span>
      ),
    },
    {
      key: 'mark_as_delivered',
      label: (
        <span className='header-menu-item'>
          <span className='header-menu-icon-slot'>
            <CheckmarkIcon />
          </span>
          <span className='header-menu-item-content'>Mark as delivered</span>
        </span>
      ),
    },
    {
      key: 'copy_shipment_link',
      label: (
        <span className='header-menu-item'>
          <span className='header-menu-icon-slot'>
            <LinkIcon width={12} height={12} />
          </span>
          <span className='header-menu-item-content'>Copy shipment link</span>
        </span>
      ),
    },
    ...(canGenerateReturnLabel
      ? [
          {
            key: 'generate_return_label',
            label: (
              <span className='header-menu-item'>
                <span className='header-menu-icon-slot'>
                  <LeftTailArrowIcon color='var(--text-primary)' />
                </span>
                <span className='header-menu-item-content'>
                  Generate return label
                </span>
              </span>
            ),
          },
        ]
      : []),
    ...(canSchedulePickup
      ? [
          {
            key: 'schedule_pickup',
            label: (
              <span className='header-menu-item'>
                <span className='header-menu-icon-slot'>
                  <CalendarIcon width={12} height={12} />
                </span>
                <span className='header-menu-item-content'>
                  Schedule pickup
                </span>
              </span>
            ),
          },
        ]
      : []),
    ...(canShowGeoFenceFeature && geoFenceLocations.length > 0
      ? [
          {
            key: 'geo_fence_locations',
            label: (
              <Flex align='center' justify='space-between' className='w-full'>
                <span className='header-menu-item'>
                  <span className='header-menu-icon-slot'>
                    <EditLocationAltIcon />
                  </span>
                  <span className='header-menu-item-content'>
                    Geo-fence locations
                  </span>
                </span>
                <ChevronRightIcon
                  fill='var(--text-primary)'
                  width={16}
                  height={16}
                />
              </Flex>
            ),
          },
        ]
      : []),
    ...(canShowRouteDeviation
      ? [
          {
            key: 'route_deviation',
            label: (
              <span className='header-menu-item'>
                <span className='header-menu-icon-slot'>
                  <RouteIcon />
                </span>
                <span className='header-menu-item-content'>
                  Route deviation
                </span>
              </span>
            ),
          },
        ]
      : []),
    ...(shipmentData?.status === 'DELIVERED' &&
    shipmentData?.temperature_gxp?.report_id &&
    GxP_permissions?.is_view &&
    enabledGxpFeature
      ? [
          {
            key: 'gxp_report',
            label: (
              <Flex align='center' justify='space-between' className='w-full'>
                <span className='header-menu-item'>
                  <span className='header-menu-icon-slot'>
                    <HomeTemperatureIcon color='var(--text-primary)' />
                  </span>
                  <span className='header-menu-item-content'>GxP report</span>
                </span>
                <ChevronRightIcon
                  fill='var(--text-primary)'
                  width={16}
                  height={16}
                />
              </Flex>
            ),
          },
        ]
      : []),
    {
      key: 'export_coc',
      label: 'Export chain of custody',
    },
  ];

  const [cOCExportCounts, setCOCExportCounts] = useState<COCExportCounts>({});

  useEffect(() => {
    if (initialAction === 'one_time_message') {
      setOpenNotificationModal(true);
    }
  }, [initialAction]);

  const fetchCOCCount = useCallback(async () => {
    const selectedIds = [shipmentId];
    const model = {
      shipment_ids: selectedIds,
    };
    GetCOCExportCount(token ?? '', model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            if (data) {
              setCOCExportCounts(data?.data?.data || {});
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  }, [token, unauthorize, shipmentId]);

  /**
   * Converts AxiosResponse<Blob> to compatible format for saveBlobAsFile
   */
  function prepareBlobForDownload(
    res: Awaited<ReturnType<typeof DownloadCOC>>
  ) {
    const headers: Record<string, string> = {};
    Object.entries(res.headers).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        headers[key] = value.join('; ');
      } else if (typeof value === 'number') {
        headers[key] = value.toString();
      } else if (value !== undefined) {
        headers[key] = value;
      }
    });

    return {
      data: res.data,
      headers,
    };
  }

  const handleClickExportCOC = async ({
    exportOptions,
  }: {
    exportOptions: Record<
      'statusUpdates' | 'comments' | 'alerts' | 'pods',
      boolean
    >;
  }) => {
    message.success(
      `${theme.themeType === 2 ? 'Transit history' : 'Chain of custody'} export started for ${cOCExportCounts?.selected_shipments} ${cOCExportCounts?.selected_shipments === 1 ? 'shipment' : 'shipments'}`
    );
    try {
      const selectedIds = [shipmentId];
      const model = {
        shipment_ids: selectedIds,
        include_status_updates: exportOptions?.statusUpdates || false,
        include_comments: exportOptions?.comments || false,
        include_alerts: exportOptions?.alerts || false,
        include_pods: exportOptions?.pods || false,
      };

      const res = await DownloadCOC(token ?? '', model); // full response
      const blobRes = prepareBlobForDownload(res);
      const zipName =
        theme.themeType === 2 ? 'Transit_history' : 'Chain_of_custody';
      saveBlobAsFile(
        blobRes,
        `${zipName}_export_${new Date().toISOString().slice(0, 10)}.zip`
      );
    } catch (err) {
      console.error(err);
      message.error(
        `We couldn’t prepare your ${themeT('chainOfCustody', theme.themeType)?.toLowerCase()} export. Please try again or contact support.`
      );
      unauthorize(err);
    }
  };

  return (
    <>
      {/* ── Back-navigation bar: visible only when viewing an EL opened from a CL ── */}

      <Flex
        gap={16}
        justify='space-between'
        className='custom-card-drawer-header detialed-shipment-header'
        style={{
          marginBottom:
            (alertsArray?.length ?? 0) > 0 || isFromHome ? '0px' : '0px',
        }}
      >
        {onBackToParent && hasOperationalLegView && (
          <Flex
            align='center'
            gap={8}
            style={{
              cursor: 'pointer',
              marginBottom: 4,
              padding: '4px 0',
            }}
            onClick={onBackToParent}
          >
            <LeftTailArrowIcon color='var(--text-primary)' />
            <TruncateWithTooltip
              text={backReferenceLabel}
              max={8}
              render={(t) => (
                <span>
                  <CustomShipmentText text={t} fontWeight={500} />
                </span>
              )}
            />
          </Flex>
        )}
        {isFromActivityFeed && (
          <Flex
            align='center'
            gap={8}
            className='cursor-pointer'
            // back to usre activity to restores panel filters/scroll/pagination.
            onClick={() => navigate(-1)}
          >
            <LeftTailArrowIcon color='var(--text-primary)' />
            <CustomShipmentText text='User activity' />
          </Flex>
        )}
        {isFromHome && (
          <span
            className='cursor-pointer hide-on-desktop'
            onClick={() => toggleDrawer(false)}
          >
            <LeftTailArrowIcon color='var(--text-primary)' />
          </span>
        )}
        <Row className='refrence-number-section'>
          <Col>
            {isFromHome && (
              <span
                className='cursor-pointer hide-on-mobile'
                onClick={() => toggleDrawer(false)}
              >
                <LeftTailArrowIcon color='var(--text-primary)' />
              </span>
            )}
          </Col>
        </Row>

        <Flex
          gap={6}
          align='center'
          className='custom-card-drawer-header-actions-buttons header-actions-buttons'
        >
          {' '}
          {allPermissions?.is_view &&
            decodedToken?.company_name?.toLowerCase() !==
              'zoomlogi support' && <CircleGroup viewers={viewers} />}
          {allPermissions?.is_view &&
            decodedToken?.company_name?.toLowerCase() !== 'zoomlogi support' &&
            legType === 'CL' &&
            (isNotificationMuted ? (
              <Tooltip title='Shipment notifications muted'>
                <span
                  data-testid='mute-bell-icon'
                  className='header-mute-bell'
                  onClick={() => {
                    toggleDrawer('open', 'notifications', shipmentId);
                  }}
                >
                  <MuteBellIcon
                    color={
                      hasNotification ? '#3D81FF' : 'var(--text-secondary)'
                    }
                    width={16}
                    height={16}
                  />
                </span>
              </Tooltip>
            ) : (
              <BellFilled
                data-testid='bell-filled-icon'
                onClick={() => {
                  toggleDrawer('open', 'notifications', shipmentId);
                }}
                style={{
                  cursor: 'pointer',
                  color: hasNotification ? '#3D81FF' : 'var(--text-secondary)',
                }}
              />
            ))}
          {allPermissions?.is_view &&
            decodedToken?.company_name?.toLowerCase() !== 'zoomlogi support' &&
            legType === 'CL' && (
              <span
                title='One Time'
                onClick={() => setOpenNotificationModal(true)}
              >
                <OneTimeArrowIcon fill='var(--text-secondary)' />
              </span>
            )}
          <span
            title='Expand map'
            onClick={goToExternalView}
            className='header-expand-map'
          >
            <FullscreenOutlined
              color='var(--text-secondary)'
              style={{ fontSize: '14px', color: 'var(--text-secondary)' }}
            />
          </span>
          {allPermissions?.is_update &&
            decodedToken?.company_name?.toLowerCase() !== 'zoomlogi support' &&
            legType === 'CL' && (
              <span title='Share' onClick={handleShareShipmentModal}>
                <UploadSmallIcon />
              </span>
            )}
          {allPermissions?.is_view &&
            decodedToken?.company_name?.toLowerCase() !== 'zoomlogi support' &&
            legType === 'CL' && (
              <Dropdown
                trigger={['click']}
                placement='bottomRight'
                open={headerMenuOpen}
                onOpenChange={(open) => {
                  setHeaderMenuOpen(open);
                  if (!open) {
                    setHeaderMenuView('main');
                  }
                }}
                dropdownRender={() =>
                  // Export COC has its own card; render it alone so the menu card doesn't show behind it.
                  headerMenuView === 'exportCoc' ? (
                    <ExportCOCDropdown
                      isDetialedView={true}
                      fetchCOCCount={fetchCOCCount}
                      cOCExportCounts={cOCExportCounts}
                      contentOnly
                      onBackClick={() => setHeaderMenuView('main')}
                      onExport={({ exportOptions }) => {
                        setHeaderMenuOpen(false);
                        setHeaderMenuView('main');
                        handleClickExportCOC({ exportOptions }).catch((error) =>
                          console.error(error)
                        );
                      }}
                    />
                  ) : (
                    <div
                      className={`custom-dropdown-export-coc ${
                        headerMenuView === 'geoFence'
                          ? 'geo-fence-location-menu'
                          : ''
                      }`}
                    >
                      {headerMenuView === 'geoFence' &&
                      canShowGeoFenceFeature ? (
                        <>
                          <button
                            type='button'
                            className='geo-fence-menu-back'
                            onClick={() => setHeaderMenuView('main')}
                          >
                            <ArrowBack
                              fill='var(--text-secondary)'
                              style={{ width: 16, height: 16, flexShrink: 0 }}
                            />
                            <span>Geo-fence locations</span>
                          </button>
                          <div
                            className='geo-fence-location-list'
                            onScroll={handleGeoFenceLocationsScroll}
                          >
                            {isGeoFenceStopsLoading &&
                              geoFenceLocations.length === 0 && (
                                <div className='geo-fence-location-state'>
                                  <Spin size='small' />
                                </div>
                              )}
                            {!isGeoFenceStopsLoading &&
                              geoFenceLocations.length === 0 && (
                                <div className='geo-fence-location-state'>
                                  No locations found
                                </div>
                              )}
                            {geoFenceLocations.map((location) => {
                              const locationText = location.menuLabel
                                ? `${location.menuLabel} ∙ ${location.name}`
                                : location.name;

                              return (
                                <div
                                  key={location.id}
                                  className='header-menu-item geo-fence-location-option'
                                  onClick={() => {
                                    setSelectedGeoFenceLocation(location);
                                    setGeoFenceModalOpen(true);
                                    setHeaderMenuOpen(false);
                                    setHeaderMenuView('main');
                                  }}
                                >
                                  <EllipsisTooltip
                                    text={locationText}
                                    className='header-menu-item-content'
                                  />
                                </div>
                              );
                            })}
                            {isGeoFenceStopsLoading &&
                              geoFenceLocations.length > 0 && (
                                <div className='geo-fence-location-state'>
                                  <Spin size='small' />
                                </div>
                              )}
                          </div>
                        </>
                      ) : (
                        items.map((item) => {
                          if (item.key === 'export_coc') {
                            return (
                              <div
                                key={item.key}
                                className='header-menu-item'
                                onClick={() => {
                                  fetchCOCCount().catch((error) =>
                                    console.error(error)
                                  );
                                  setHeaderMenuView('exportCoc');
                                }}
                              >
                                <Flex
                                  align='center'
                                  justify='space-between'
                                  className='w-full'
                                >
                                  <span className='header-menu-item'>
                                    <span className='header-menu-icon-slot'>
                                      <CopyIcon />
                                    </span>
                                    <span className='header-menu-item-content'>
                                      {`Export ${themeT(
                                        'chainOfCustody',
                                        theme.themeType
                                      )?.toLowerCase()}`}
                                    </span>
                                  </span>
                                  <ChevronRightIcon
                                    fill='var(--text-primary)'
                                    width={16}
                                    height={16}
                                  />
                                </Flex>
                              </div>
                            );
                          }

                          if (item.key === 'geo_fence_locations') {
                            if (!featuresPermissions?.is_update) return null;

                            return (
                              <div
                                key={item.key}
                                className='header-menu-item'
                                onClick={() => setHeaderMenuView('geoFence')}
                              >
                                {item.label}
                              </div>
                            );
                          }

                          if (item.key === 'route_deviation') {
                            return (
                              <div
                                key={item.key}
                                className='header-menu-item'
                                onClick={() => {
                                  setHeaderMenuOpen(false);
                                  setRouteDeviationModalOpen(true);
                                }}
                              >
                                {item.label}
                              </div>
                            );
                          }

                          const onItemClick = (): void => {
                            setHeaderMenuOpen(false);
                            handleMenuClick({ key: item.key });
                          };

                          // Copying a link is read-only; show it to all users
                          if (item.key === 'copy_shipment_link') {
                            return (
                              <div
                                key={item.key}
                                className='header-menu-item'
                                onClick={onItemClick}
                              >
                                {item.label}
                              </div>
                            );
                          }

                          // Edit reflects this shipment's own permissions,
                          // not the global "Record management" feature flag.
                          if (item.key === 'edit_shipment') {
                            if (!shipmentData?.permissions?.is_update)
                              return null;

                            return (
                              <div
                                key={item.key}
                                className='header-menu-item'
                                onClick={onItemClick}
                              >
                                {item.label}
                              </div>
                            );
                          }

                          // Archive / mark as delivered reflect this shipment's
                          // own record-management flag, not the global feature.
                          if (
                            item.key === 'archive_shipment' ||
                            item.key === 'mark_as_delivered'
                          ) {
                            if (shipmentData?.can_manage_record !== true)
                              return null;

                            return (
                              <div
                                key={item.key}
                                className='header-menu-item'
                                onClick={onItemClick}
                              >
                                {item.label}
                              </div>
                            );
                          }

                          // Render other items only if update permission exists
                          if (!featuresPermissions?.is_update) return null;

                          return (
                            <div
                              key={item.key}
                              className='header-menu-item'
                              onClick={onItemClick}
                            >
                              {item.label}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )
                }
              >
                <span>
                  <DotsIcon />
                </span>
              </Dropdown>
            )}
          <span
            title='Close'
            className='no-bg'
            onClick={() => toggleDrawer(false, 'all')}
          >
            <CloseOutlined
              color='var(--text-primary)'
              style={{ fontSize: '14px', color: 'var(--text-primary)' }}
            />
          </span>
        </Flex>
      </Flex>
      <Flex vertical style={{ marginTop: '-17px' }}>
        <CustomShipmentText
          text={referenceLabel}
          color='#767676'
          fontWeight={600}
        />

        <div
          className={`ref-container ${
            (alertsArray?.length ?? 0) > 0
              ? 'ref-container--with-alerts'
              : 'ref-container--no-alerts'
          }`}
        >
          <Flex vertical gap={0} style={{ minWidth: 0 }}>
            <Flex align='center' gap={8}>
              {legType === 'EL' && hasOperationalLegView ? (
                <span
                  className='shipment-grid-reference-leg-icon header-leg-icon'
                  aria-hidden='true'
                >
                  {getShipmentLegModeIcon(
                    getLegModeIconType(legMode?.name ?? legMode?.value ?? '')
                  )}
                </span>
              ) : null}
              {(referenceNumber?.length || 0) > 23 ? (
                <Tooltip title={referenceNumber}>
                  <span>
                    <CustomShipmentText
                      text={truncateByLength(referenceNumber, 23)}
                      marginTop={'0px'}
                      className='reference-id-wrapper'
                    />
                  </span>
                </Tooltip>
              ) : (
                <CustomShipmentText
                  text={referenceNumber || ''}
                  className='reference-id-wrapper'
                  marginTop={'0px'}
                />
              )}
              <Flex gap={4}>
                {teams?.length > 0 && (
                  <>
                    <TeamListWithDots teams={teams} />
                  </>
                )}
              </Flex>
            </Flex>
            {legContextLabel && hasOperationalLegView ? (
              <CustomShipmentText
                text={legContextLabel}
                color='rgb(118, 118, 118)'
                fontWeight={600}
                marginTop='0px'
              />
            ) : null}
          </Flex>
          <>
            {/* <Flex gap={4}>
              {teams?.length > 0 && (
                <>
                  <TeamListWithDots teams={teams} />
                </>
              )}
            </Flex> */}
          </>
        </div>
        <FedExPickupControls
          shipment={shipmentData}
          onViewDetails={
            supportsSchedulePickup
              ? () => handleMenuClick({ key: 'view_pickup_details' })
              : undefined
          }
        />
        {isShell && (
          <Flex vertical gap={8} style={{ marginTop: 4, marginBottom: 16 }}>
            <Button
              className='shipment-header-quote-btn'
              type='primary'
              onClick={onQuoteShell}
              disabled={!onQuoteShell}
            >
              Quote
            </Button>
            <Tooltip
              title={
                onBookShell
                  ? undefined
                  : 'Book is not available for this shipment.'
              }
            >
              <span className='header-book-wrap'>
                <Button
                  onClick={onBookShell}
                  disabled={!onBookShell}
                  className='header-book-btn'
                >
                  Book
                </Button>
              </span>
            </Tooltip>
          </Flex>
        )}
      </Flex>
      {openNotificationModal && (
        <PerShipmentNotificationModal
          // eslint-disable-next-line no-restricted-syntax -- modal's `selected` prop is non-nullable but null means "new"
          selected={null as unknown as NotificationData}
          fetchNotifications={() => {}}
          shipmentId={shipmentId}
          notificationModalType={2}
          openNotificationModal={openNotificationModal}
          setOpenNotificationModal={setOpenNotificationModal}
        />
      )}
      {canShowRouteDeviation && (
        <RouteDeviationSetupModal
          open={routeDeviationModalOpen}
          shipmentId={shipmentId}
          pickup={routeDeviationLane.pickup}
          delivery={routeDeviationLane.delivery}
          currentPosition={routeDeviationPosition}
          onClose={() => setRouteDeviationModalOpen(false)}
        />
      )}
      {canShowGeoFenceFeature && (
        <GeoFenceLocationsModal
          open={geoFenceModalOpen}
          shipmentId={shipmentId}
          location={selectedGeoFenceLocation}
          savedFence={
            selectedGeoFenceLocation
              ? geoFenceDrafts[selectedGeoFenceLocation.id]
              : undefined
          }
          onClose={() => setGeoFenceModalOpen(false)}
          onApply={(locationId, draft) => {
            setGeoFenceDrafts((current) => ({
              ...current,
              [locationId]: draft,
            }));
            refetchGeoFenceStops();
          }}
          onRemoved={(locationId) => {
            setGeoFenceDrafts((current) => {
              const next = { ...current };
              delete next[locationId];
              return next;
            });
            refetchGeoFenceStops();
          }}
        />
      )}
    </>
  );
};

export default ShipmentHeader;
